import { jsonrepair } from 'jsonrepair';

/**
 * Universal schema parser for react-json-view.
 *
 * Handles three input formats:
 *  1) Valid JSON (including nested schema strings, as in a skill response).
 *  2) Python literal: single quotes, with possibly unescaped double quotes
 *     and backticks inside the strings.
 *  3) Physically broken JSON (unescaped quotes) — repaired via jsonrepair.
 *
 * Values are NOT coerced to other types: "True" stays the string "True"
 * (important for enums like ['True','False','Skip'] and service fields).
 */

export interface ParseOptions {
    /**
     * Expand string values that are themselves a JSON/Python literal
     * (start with `{` or `[`) into nested objects. Defaults to true.
     */
    expandNestedJsonStrings?: boolean;
    /** Max recursion depth for expansion. Guards against pathological inputs. */
    maxDepth?: number;
}

const DEFAULT_OPTIONS: Required<ParseOptions> = {
    expandNestedJsonStrings: true,
    maxDepth: 8
};

const ESCAPE_SEQUENCE_LENGTH = 2;

interface ParseResult {
    value: string;
    nextIndex: number;
}

/** Resolves an escape sequence inside a string literal. */
function unescapeChar(quote: string, next: string): string {
    if (next === quote) {
        return quote;
    }

    switch (next) {
        case '\\': {
            return '\\';
        }
        case 'n': {
            return '\n';
        }
        case 't': {
            return '\t';
        }
        case 'r': {
            return '\r';
        }
        default: {
            return next;
        }
    }
}

/** Parses a string literal and re-emits it via JSON.stringify. */
function parseStringLiteral(input: string, start: number): ParseResult {
    const quote = input[start];
    let index = start + 1;
    let str = '';

    while (index < input.length) {
        const char = input[index];

        if (char === '\\') {
            str += unescapeChar(quote, input[index + 1]);
            index += ESCAPE_SEQUENCE_LENGTH;
            continue;
        }

        if (char === quote) {
            index += 1;
            break;
        }

        str += char;
        index += 1;
    }

    // proper escaping for JSON
    return { value: JSON.stringify(str), nextIndex: index };
}

/** Parses a bare (unquoted) identifier: the Python constants True/False/None. */
function parseIdentifier(input: string, start: number): ParseResult {
    let index = start;
    let word = '';

    while (index < input.length && /\w/.test(input[index])) {
        word += input[index];
        index += 1;
    }

    switch (word) {
        case 'True': {
            return { value: 'true', nextIndex: index };
        }
        case 'False': {
            return { value: 'false', nextIndex: index };
        }
        case 'None': {
            return { value: 'null', nextIndex: index };
        }
        default: {
            return { value: word, nextIndex: index };
        }
    }
}

/**
 * Rewrites a Python-like literal into JSON-compatible text.
 * Strings are re-emitted via JSON.stringify, so nested double quotes and
 * backticks inside a single-quoted string do not break the structure.
 */
function pythonLiteralToJson(input: string): string {
    let index = 0;
    let out = '';

    while (index < input.length) {
        const char = input[index];

        // String literal (single or double quotes)
        if (char === '"' || char === "'") {
            const { value, nextIndex } = parseStringLiteral(input, index);
            out += value;
            index = nextIndex;
            continue;
        }

        // Identifiers outside strings: Python constants
        if (/[A-Z_]/i.test(char)) {
            const { value, nextIndex } = parseIdentifier(input, index);
            out += value;
            index = nextIndex;
            continue;
        }

        out += char;
        index += 1;
    }

    return out;
}

// ── Inner-quote escaper (last-resort heuristic) ────────────────────────────────

/** Characters that may legitimately follow a closing string delimiter. */
const STRUCTURAL_AFTER_STRING = new Set([',', '}', ']', ':']);

/** Returns the next non-whitespace character starting at `from` (or '' at EOF). */
function nextMeaningfulChar(input: string, from: number): string {
    let index = from;

    while (index < input.length) {
        const char = input[index];
        if (char !== ' ' && char !== '\n' && char !== '\r' && char !== '\t') {
            return char;
        }
        index += 1;
    }

    return '';
}

interface QuoteStep {
    /** Text to append to the output. */
    chunk: string;
    /** How many input characters were consumed. */
    consumed: number;
    /** Whether this step closes the current string. */
    closesString: boolean;
}

/** Processes a single character while inside a string value. */
function stepInsideString(input: string, index: number): QuoteStep {
    const char = input[index];

    // Preserve existing escape sequences as-is.
    if (char === '\\') {
        const escaped = index + 1 < input.length ? input[index + 1] : '';
        return {
            chunk: char + escaped,
            consumed: ESCAPE_SEQUENCE_LENGTH,
            closesString: false
        };
    }

    if (char === '"') {
        const next = nextMeaningfulChar(input, index + 1);
        const closes = next === '' || STRUCTURAL_AFTER_STRING.has(next);
        return {
            chunk: closes ? '"' : String.raw`\"`,
            consumed: 1,
            closesString: closes
        };
    }

    return { chunk: char, consumed: 1, closesString: false };
}

/**
 * Escapes unescaped double quotes that appear *inside* string values.
 */
function escapeInnerQuotes(input: string): string {
    let out = '';
    let inString = false;
    let index = 0;

    while (index < input.length) {
        const char = input[index];

        if (!inString) {
            out += char;
            inString = char === '"';
            index += 1;
            continue;
        }

        const step = stepInsideString(input, index);
        out += step.chunk;
        index += step.consumed;
        if (step.closesString) {
            inString = false;
        }
    }

    return out;
}

/** Raw string normalization strategies applied before JSON.parse (in order). */
const PARSE_STRATEGIES: ((raw: string) => string)[] = [
    (raw) => raw,
    pythonLiteralToJson,
    jsonrepair,
    (raw) => jsonrepair(escapeInnerQuotes(raw))
];

/** Tries to parse a string into a value by walking the strategy chain. */
function parseStringToValue(raw: string): unknown {
    let result: unknown;

    for (const normalize of PARSE_STRATEGIES) {
        try {
            result = JSON.parse(normalize(raw));
            break;
        } catch {
            /* try the next strategy */
        }
    }

    return result;
}

/** Whether a string value looks like a nested JSON/Python literal. */
function looksLikeNestedStructure(value: string): boolean {
    const trimmed = value.trim();
    return trimmed.startsWith('{') || trimmed.startsWith('[');
}

/** Expands a string value if it looks like a nested structure. */
function expandNestedString(
    value: string,
    depth: number,
    max: number,
    expand: (value: unknown, depth: number, max: number) => unknown
): unknown {
    let result: unknown = value;

    if (looksLikeNestedStructure(value)) {
        const parsed = parseStringToValue(value);
        if (parsed !== undefined) {
            result = expand(parsed, depth + 1, max);
        }
    }

    return result;
}

/** Recursively expands structure-like strings into nested objects. */
function expandNested(value: unknown, depth: number, max: number): unknown {
    let result: unknown = value;

    if (depth < max) {
        if (typeof value === 'string') {
            result = expandNestedString(value, depth, max, expandNested);
        } else if (Array.isArray(value)) {
            result = value.map((item) => expandNested(item, depth + 1, max));
        } else if (value !== null && typeof value === 'object') {
            const entries = Object.entries(value as Record<string, unknown>);
            result = Object.fromEntries(
                entries.map(([key, item]) => [
                    key,
                    expandNested(item, depth + 1, max)
                ])
            );
        }
    }

    return result;
}

/**
 * Coerces any of the supported formats into an object.
 * Idempotent: calling it again with an already-parsed object is safe.
 *
 * NOTE: This client-side normalization exists only because the backend
 * currently returns prompt schemas in inconsistent formats (valid JSON,
 * Python literals with single quotes, and physically broken JSON). The BE
 * cannot be changed for now, so we have to repair the payload on the frontend
 * to render it in react-json-view. Once the backend emits canonical JSON,
 * this function (and the whole converter) should be removed.
 *
 * @returns object/array for react-json-view, or undefined if parsing failed.
 */
export function parseSchema(
    input: unknown,
    options: ParseOptions = {}
): unknown {
    const options_ = { ...DEFAULT_OPTIONS, ...options };
    let result: unknown;

    // Already a structure — nothing to parse (only optionally expand nested)
    if (input !== null && typeof input === 'object') {
        result = options_.expandNestedJsonStrings
            ? expandNested(input, 0, options_.maxDepth)
            : input;
    } else if (typeof input === 'string' && input.trim() !== '') {
        const parsed = parseStringToValue(input.trim());

        if (parsed === undefined) {
            console.error('parseSchema: failed to parse input', { input });
        } else {
            result = options_.expandNestedJsonStrings
                ? expandNested(parsed, 0, options_.maxDepth)
                : parsed;
        }
    }

    return result;
}
